import { parseAsInteger, useQueryState } from "nuqs";
import { type FC, type PropsWithChildren, useEffect, useMemo, useRef, useState } from "react";

import { initialPaginationState, PaginationContext } from "./use-pagination";

export interface PaginationProviderProps {
  numRecords?: number;
  page?: number;
  rowsPerPage?: string;
  rowsPerPageOptions?: string[];
  shouldResetPageOnNumRecordsChange?: boolean;
  shouldResetPageOnRowsPerPageChange?: boolean;
}

export const PaginationProvider: FC<PropsWithChildren<PaginationProviderProps>> = ({
  children,
  shouldResetPageOnNumRecordsChange = true,
  shouldResetPageOnRowsPerPageChange = true,
  ...init
}) => {
  const [numRecords, setNumRecords] = useState(init?.numRecords || initialPaginationState.numRecords);
  const [page, setPage] = useQueryState("page", parseAsInteger.withDefault(init.page || initialPaginationState.page));
  const [rowsPerPage, setRowsPerPage] = useQueryState("rowsPerPage", {
    defaultValue: init.rowsPerPage || initialPaginationState.rowsPerPage,
  });
  const prevNumRecordsRef = useRef<number>(0);

  useEffect(() => {
    // If the number of records is less than the first record on the current page, reset the page to the last page.
    // This is to prevent the user from being on a page that no longer exists (i.e. if the number of records is
    // reduced as a result of a user deleting a record).
    if (numRecords && numRecords < page * Number(rowsPerPage) - (Number(rowsPerPage) - 1)) {
      void setPage(Math.max(1, Math.ceil(numRecords / Number(rowsPerPage))));
    }
  }, [numRecords, page, rowsPerPage, setPage]);

  // Reset page when numRecords changes from a non-zero value (indicates filter/data change, not initial load)
  useEffect(() => {
    if (
      shouldResetPageOnNumRecordsChange &&
      numRecords !== prevNumRecordsRef.current &&
      prevNumRecordsRef.current !== 0
    ) {
      void setPage(1);
    }
    prevNumRecordsRef.current = numRecords;
  }, [numRecords, shouldResetPageOnNumRecordsChange, setPage]);

  useEffect(() => {
    if (shouldResetPageOnRowsPerPageChange) {
      void setPage(1);
    }
  }, [rowsPerPage, shouldResetPageOnRowsPerPageChange, setPage]);

  const numPages = useMemo(() => Math.ceil(numRecords / Number(rowsPerPage)), [numRecords, rowsPerPage]);

  const value = useMemo(
    () => ({
      numPages: numPages || 1,
      numRecords,
      page,
      rowsPerPage,
      rowsPerPageOptions: init.rowsPerPageOptions || initialPaginationState.rowsPerPageOptions,
      setNumRecords,
      setPage,
      setRowsPerPage,
    }),
    [numPages, numRecords, page, rowsPerPage, init.rowsPerPageOptions, setPage, setRowsPerPage]
  );

  return <PaginationContext.Provider value={value}>{children}</PaginationContext.Provider>;
};
